import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  ButtonGroup,
  Flex,
  Container,
  Card,
  CardHeader,
  CardBody,
  Tooltip,
} from '@chakra-ui/react';
import {
  FaUsers,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaUserShield,
  FaUserEdit,
  FaEye,
} from 'react-icons/fa';
import { AdminOnly } from './PermissionGuard';
import LimitRestrictedButton from './LimitRestrictedButton';
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser } from '../hooks/useUsers';
import { useInvites, useCreateInvite, useRevokeInvite } from '../hooks/useInvites';
import { useAuth } from '../contexts/AuthContext';
import { formatDateDMY } from '../utils/formatDate';
import UsageDashboard from './UsageDashboard';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  createdAt: string;
  updatedAt: string;
}

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: 'admin' | 'editor' | 'viewer';
}

const ManageUsers: React.FC = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const toast = useToast();
  const { user: currentUser } = useAuth();

  // Hooks
  const { data: users, ownerId, isLoading, error } = useUsers();
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();
  const { data: invites, isLoading: invitesLoading } = useInvites();
  const createInviteMutation = useCreateInvite();
  const revokeInviteMutation = useRevokeInvite();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'editor' | 'viewer'>('editor');

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<UserFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'viewer',
  });
  const [formErrors, setFormErrors] = useState<Partial<UserFormData>>({});

  // Modal controls
  const {
    isOpen: isUserModalOpen,
    onOpen: onUserModalOpen,
    onClose: onUserModalClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  const isWorkspaceOwner = (userId: string) => !!ownerId && ownerId === userId;

  const canEditUser = (user: User) => {
    if (!isWorkspaceOwner(user._id)) return true;
    return currentUser?._id === user._id;
  };

  const canDeleteUser = (user: User) => !isWorkspaceOwner(user._id);

  // Filter users based on search term and role
  const filteredUsers =
    users?.filter((user: User) => {
      const matchesSearch =
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesRole = roleFilter === 'all' || user.role === roleFilter;

      return matchesSearch && matchesRole;
    }) || [];

  // Form validation
  const validateForm = () => {
    const errors: Partial<UserFormData> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!isEditing && !formData.password.trim()) {
      errors.password = 'Password is required';
    } else if (!isEditing && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Modal handlers
  const handleCreateUser = () => {
    setIsEditing(false);
    setSelectedUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'viewer',
    });
    setFormErrors({});
    onUserModalOpen();
  };

  const handleEditUser = (user: User) => {
    if (!canEditUser(user)) return;
    setIsEditing(true);
    setSelectedUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      password: '',
      role: user.role,
    });
    setFormErrors({});
    onUserModalOpen();
  };

  const handleDeleteUser = (user: User) => {
    if (!canDeleteUser(user)) return;
    setSelectedUser(user);
    onDeleteModalOpen();
  };

  // Form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (isEditing && selectedUser) {
        const updateData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: formData.role,
          ...(formData.password.trim() && { password: formData.password }),
        };

        await updateUserMutation.mutateAsync({
          id: selectedUser._id,
          userData: updateData,
        });

        toast({
          title: 'Success',
          description: 'User updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await createUserMutation.mutateAsync(formData);

        toast({
          title: 'Success',
          description: 'User created successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      onUserModalClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error?.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} user`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Delete confirmation
  const confirmDelete = async () => {
    if (!selectedUser) return;

    try {
      await deleteUserMutation.mutateAsync(selectedUser._id);

      toast({
        title: 'Success',
        description: 'User deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onDeleteModalClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to delete user',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Role badge color
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'red';
      case 'editor':
        return 'green';
      case 'viewer':
        return 'blue';
      default:
        return 'gray';
    }
  };

  // Role icon
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return FaUserShield;
      case 'editor':
        return FaUserEdit;
      case 'viewer':
        return FaEye;
      default:
        return FaUsers;
    }
  };

  if (error) {
    return (
      <Container maxW="7xl" p={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          Error loading users: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <AdminOnly showFallback>
      <Container maxW="100%" w="100%" p={0}>
        <VStack align="stretch" spacing={6} mb={6}>
          <UsageDashboard />
          <Card bg={bgColor} shadow="md" borderRadius="xl">
            <CardHeader>
              <Heading size="md">Invite teammates</Heading>
              <Text fontSize="sm" color="gray.600" mt={1}>
                Send an email invite instead of creating accounts manually
              </Text>
            </CardHeader>
            <CardBody pt={0}>
              <HStack align="flex-end" flexWrap="wrap" gap={3}>
                <FormControl flex="1" minW="200px">
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@company.com"
                  />
                </FormControl>
                <FormControl w="140px">
                  <FormLabel>Role</FormLabel>
                  <Select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as any)}>
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </Select>
                </FormControl>
                <LimitRestrictedButton
                  resource="users"
                  colorScheme="blue"
                  isLoading={createInviteMutation.isPending}
                  onClick={() => {
                    if (!inviteEmail.trim()) return;
                    createInviteMutation.mutate(
                      { email: inviteEmail.trim(), role: inviteRole },
                      {
                        onSuccess: (res) => {
                          toast({
                            title: 'Invite sent',
                            description: res.invite?.inviteUrl
                              ? 'Share the link if email delivery fails.'
                              : undefined,
                            status: 'success',
                          });
                          setInviteEmail('');
                        },
                        onError: (err: any) => {
                          toast({
                            title: err.response?.data?.message || 'Invite failed',
                            status: 'error',
                          });
                        },
                      }
                    );
                  }}
                >
                  Send invite
                </LimitRestrictedButton>
              </HStack>
              {invites && invites.length > 0 && (
                <Box mt={4}>
                  <Text fontWeight="semibold" fontSize="sm" mb={2}>
                    Pending invites
                  </Text>
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Email</Th>
                        <Th>Role</Th>
                        <Th>Expires</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {invites.map((inv) => (
                        <Tr key={inv._id}>
                          <Td>{inv.email}</Td>
                          <Td>
                            <Badge>{inv.role}</Badge>
                          </Td>
                          <Td>{new Date(inv.expiresAt).toLocaleDateString()}</Td>
                          <Td>
                            <Button
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              isLoading={revokeInviteMutation.isPending}
                              onClick={() => revokeInviteMutation.mutate(inv._id)}
                            >
                              Revoke
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </CardBody>
          </Card>
        </VStack>
        <Card bg={bgColor} shadow="lg" borderRadius="xl">
          <CardHeader>
            <Flex justify="space-between" align="center">
              <HStack spacing={4}>
                <Icon as={FaUsers} boxSize={8} color="blue.500" />
                <Heading size="lg" color="gray.800">
                  Manage Users
                </Heading>
              </HStack>
              <LimitRestrictedButton
                resource="users"
                leftIcon={<Icon as={FaPlus} />}
                colorScheme="blue"
                onClick={handleCreateUser}
                isLoading={createUserMutation.isPending}
              >
                Add New User
              </LimitRestrictedButton>
            </Flex>
          </CardHeader>

          <CardBody>
            {/* Filters */}
            <Flex gap={4} mb={6} direction={{ base: 'column', md: 'row' }}>
              {/* <InputGroup maxW="300px">
                <InputLeftElement>
                  <Icon as={FaSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </InputGroup> */}

              <Select maxW="200px" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </Select>
            </Flex>

            {/* Users Table */}
            {isLoading ? (
              <Flex justify="center" py={12}>
                <Spinner size="xl" color="blue.500" />
              </Flex>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Role</Th>
                      <Th>Created</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredUsers.map((user: User) => {
                      const editable = canEditUser(user);
                      const deletable = canDeleteUser(user);
                      const owner = isWorkspaceOwner(user._id);

                      return (
                      <Tr key={user._id} _hover={{ bg: 'gray.50' }}>
                        <Td>
                          <HStack>
                            <Icon as={getRoleIcon(user.role)} color="gray.500" />
                            <Text fontWeight="medium">
                              {user.firstName} {user.lastName}
                            </Text>
                            {owner && (
                              <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                                Owner
                              </Badge>
                            )}
                          </HStack>
                        </Td>
                        <Td>{user.email}</Td>
                        <Td>
                          <Badge
                            colorScheme={getRoleBadgeColor(user.role)}
                            variant="subtle"
                            fontSize="sm"
                            px={3}
                            py={1}
                            borderRadius="full"
                          >
                            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color="gray.600">
                            {user.createdAt ? formatDateDMY(user.createdAt) : '—'}
                          </Text>
                        </Td>
                        <Td>
                          <ButtonGroup size="sm" spacing={2}>
                            <Tooltip
                              label={
                                editable
                                  ? 'Edit user'
                                  : 'Only the workspace owner can edit their account'
                              }
                              isDisabled={editable}
                            >
                              <Button
                                leftIcon={<Icon as={FaEdit} />}
                                colorScheme="blue"
                                variant="outline"
                                onClick={() => handleEditUser(user)}
                                isDisabled={!editable}
                              >
                                Edit
                              </Button>
                            </Tooltip>
                            <Tooltip
                              label={
                                deletable
                                  ? 'Delete user'
                                  : 'The workspace owner cannot be deleted'
                              }
                              isDisabled={deletable}
                            >
                              <Button
                                leftIcon={<Icon as={FaTrash} />}
                                colorScheme="red"
                                variant="outline"
                                onClick={() => handleDeleteUser(user)}
                                isDisabled={!deletable}
                              >
                                Delete
                              </Button>
                            </Tooltip>
                          </ButtonGroup>
                        </Td>
                      </Tr>
                    );
                    })}
                  </Tbody>
                </Table>

                {filteredUsers.length === 0 && (
                  <Flex justify="center" py={12}>
                    <Text color="gray.500" fontSize="lg">
                      {searchTerm || roleFilter !== 'all'
                        ? 'No users match your search criteria'
                        : 'No users found'}
                    </Text>
                  </Flex>
                )}
              </Box>
            )}
          </CardBody>
        </Card>

        {/* User Modal */}
        <Modal isOpen={isUserModalOpen} onClose={onUserModalClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{isEditing ? 'Edit User' : 'Create New User'}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isInvalid={!!formErrors.firstName}>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    value={formData.firstName}
                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Enter first name"
                  />
                  <FormErrorMessage>{formErrors.firstName}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!formErrors.lastName}>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    value={formData.lastName}
                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                  <FormErrorMessage>{formErrors.lastName}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!formErrors.email}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email address"
                  />
                  <FormErrorMessage>{formErrors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!formErrors.password}>
                  <FormLabel>
                    Password{' '}
                    {isEditing && (
                      <Text as="span" fontSize="sm" color="gray.500">
                        (leave blank to keep current)
                      </Text>
                    )}
                  </FormLabel>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    placeholder={isEditing ? 'Enter new password (optional)' : 'Enter password'}
                  />
                  <FormErrorMessage>{formErrors.password}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Role</FormLabel>
                  <Select
                    value={formData.role}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        role: e.target.value as 'admin' | 'editor' | 'viewer',
                      })
                    }
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </Select>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onUserModalClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={createUserMutation.isPending || updateUserMutation.isPending}
              >
                {isEditing ? 'Update User' : 'Create User'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Confirm Delete</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>
                Are you sure you want to delete user{' '}
                <Text as="span" fontWeight="bold">
                  {selectedUser?.firstName} {selectedUser?.lastName}
                </Text>
                ? This action cannot be undone.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                isLoading={deleteUserMutation.isPending}
              >
                Delete User
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Container>
    </AdminOnly>
  );
};

export default ManageUsers;
